import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import ModuleCard from '../../components/common/ModuleCard';
import VideoLightbox from '../../components/common/VideoLightbox';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { SCHOOL_OVERVIEW } from '../../lib/mockSchool';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { Check, PlayCircle, ArrowRight, Sparkles, Plus, Calculator } from 'lucide-react';
import * as Icons from 'lucide-react';

const STUDENT_CLIPS = [
  'JIXILoMWUlw',
  'tnBRe1eVryY',
  'rU3FY-9OVXo',
];
const TEAM_PHOTO_URL = 'https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/1bootw0n_IMG-20260522-WA0004.jpg';
const LAPTOP_URL     = 'https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/43lild4i_Gyan%20Dhara%20web%20learning%20experience.png';

const colorText = { purple: 'text-purple', royal: 'text-royal', cyan: 'text-cyan', mint: 'text-mint' };
const colorBg50 = { purple: 'bg-purple-50', royal: 'bg-royal-50', cyan: 'bg-cyan-50', mint: 'bg-mint-50' };
const colorRing = { purple: 'ring-purple/20', royal: 'ring-royal/20', cyan: 'ring-cyan/20', mint: 'ring-mint/20' };

function EstimateCalculator({ d }) {
  const [n, setN] = useState('');
  const [result, setResult] = useState('');
  const calc = () => {
    const s = parseInt(n, 10);
    if (!s || s <= 0) { setResult(''); return; }
    const headsets = Math.max(3, Math.ceil(s / 20));
    setResult(`${headsets} headsets for ${s} students`);
  };
  return (
    <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
      <label htmlFor="est-input" className="text-[13px] font-semibold text-ink-700">{d.inputLabel}</label>
      <div className="mt-3 flex gap-2">
        <input
          id="est-input"
          type="number"
          min="1"
          value={n}
          onChange={(e) => setN(e.target.value)}
          placeholder={d.inputPlaceholder}
          data-testid="estimate-input"
          className="flex-1 rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy focus:outline-none focus:ring-2 focus:ring-purple/40"
        />
        <button onClick={calc} data-testid="estimate-calculate" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-soft hover:brightness-110">
          <Calculator className="h-4 w-4" /> {d.buttonLabel}
        </button>
      </div>
      <div className="mt-5 rounded-xl bg-mint-50 p-4 ring-1 ring-mint/20">
        <div className="text-[11px] font-bold tracking-widest text-mint-600">{d.resultLabel.toUpperCase()}</div>
        <div className="mt-1 text-[20px] font-extrabold text-navy" data-testid="estimate-result">{result || d.resultExample}</div>
      </div>
    </div>
  );
}

export default function SchoolOverview() {
  const d = SCHOOL_OVERVIEW;
  const [heroOpen, setHeroOpen] = useState(false);
  const [openClip, setOpenClip] = useState(null);
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="school-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
          <div className="mt-4 grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>

              {/* No licence fee pill — heavily highlighted */}
              <div className="mt-6" data-testid="hero-licence-fee-highlight"><LicenceFeeHighlight text={d.hero.pill} /></div>

              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={d.hero.ctaPrimary.to} data-testid="hero-start-deployment">{d.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={d.hero.ctaSecondary.to} variant="outline" data-testid="hero-request-demo">{d.hero.ctaSecondary.label}</GradientButton>
              </div>

              {/* Partner badges — Figma layout: monogram + label pair */}
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hero-partners">
                {d.hero.partners.map((p, i) => (
                  <div key={i} className="inline-flex items-center gap-2.5 rounded-xl border border-canvas-200 bg-white px-3 py-2 shadow-soft">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-navy to-royal text-white text-[11px] font-bold tracking-wider">{p.label.split(' ').map(w => w[0]).slice(0, 3).join('')}</span>
                    <span className="text-[12px] font-semibold text-navy max-w-[220px] leading-tight">{p.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[11.5px] text-ink-500 italic">{d.hero.signOff}</div>
            </div>

            {/* Hero video — "View Gyan Dhara in action" */}
            <div className="rounded-3xl border border-canvas-200 bg-white p-3 shadow-soft overflow-hidden">
              <button type="button" onClick={() => setHeroOpen(true)} data-testid="school-hero-video-play" aria-label="View Gyan Dhara in action" className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30">
                <img src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')} alt="View Gyan Dhara in action" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 shadow-soft group-hover:scale-105 transition-transform"><PlayCircle className="h-8 w-8 text-navy" /></span></span>
              </button>
              <div className="px-3 pb-3 pt-4 text-center text-[13px] font-semibold text-navy">{d.hero.videoLabel}</div>
            </div>
          </div>
        </div>
      </section>

      {/* THE LEARNING SYSTEM — three layers */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.layers.eyebrow} title={d.layers.title} desc={d.layers.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.layers.items.map((l, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card" data-testid={`layer-card-${i}`}>
                <div className="flex items-center gap-3">
                  <IconBadge icon={l.icon} color={l.color} size="lg" />
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-[13px] font-bold ${colorBg50[l.color]} ${colorText[l.color]}`}>{l.key}</span>
                </div>
                <h3 className="mt-4 text-[18px] font-bold text-navy">{l.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PURPOSE */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.purpose.eyebrow} title={d.purpose.title} desc={d.purpose.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.purpose.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                <IconBadge icon={p.icon} color={p.color} />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NCERT CURRICULUM LEARNING */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.curriculum.eyebrow} title={d.curriculum.title} desc={d.curriculum.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {d.curriculum.available.map((m, i) => (
              <ModuleCard key={i} subject={m.subject} status={m.status} title={m.title} desc={m.chapter} duration="35–45 min" lang="EN / HI / AS" />
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-canvas-100 p-5 border border-canvas-200">
            <div className="text-[11.5px] font-bold tracking-widest text-purple">{d.curriculum.inDev.label.toUpperCase()}</div>
            <div className="mt-3 text-[14px] font-semibold text-navy">{d.curriculum.inDev.items}</div>
            <div className="mt-2 text-[12.5px] text-ink-500">{d.curriculum.inDev.note}</div>
          </div>
          <div className="mt-8 flex justify-center">
            <GradientButton to="/school/experiences" variant="outline" data-testid="cta-view-all-experiences">View All Learning Experiences <ArrowRight className="h-4 w-4" /></GradientButton>
          </div>
        </div>
      </section>

      {/* EXPLORE BEYOND THE TEXTBOOK — Gyan Mitra and Gyan Vatikas */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.beyond.eyebrow} title={d.beyond.title} desc={d.beyond.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {d.beyond.items.map((m, i) => (
              <ModuleCard key={i} subject={m.subject} status={m.status} title={m.title} desc={m.chapter} duration="45–60 min" lang="EN / HI / AS" />
            ))}
          </div>
        </div>
      </section>

      {/* CLASSROOM USE — Designed to fit within the school timetable */}
      <section className="px-6 py-16" data-testid="timetable-section">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.timetable.eyebrow} title={d.timetable.title} desc={d.timetable.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.timetable.steps.map((s) => {
              const Icon = Icons[s.icon] || Icons.Circle;
              return (
                <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                  <div className="flex items-center gap-3">
                    <IconBadge icon={s.icon} color={s.color} size="lg" />
                    <StepChip n={s.n} color={s.color} />
                  </div>
                  <h3 className="mt-4 text-[15.5px] font-bold text-navy">{s.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SETUP — Estimate the setup your school may need */}
      <section className="px-6 py-16 bg-white" data-testid="estimate-section">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.estimate.eyebrow} title={d.estimate.title} desc={d.estimate.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EstimateCalculator d={d.estimate} />
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-6 shadow-soft flex flex-col justify-center">
              <div className="text-[18px] font-bold text-navy">{d.estimate.tailored.title}</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{d.estimate.tailored.desc}</p>
              <div className="mt-4">
                <GradientButton to={d.estimate.tailored.cta.to} variant="dark" data-testid="estimate-custom-cta">{d.estimate.tailored.cta.label} <ArrowRight className="h-4 w-4" /></GradientButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTITUTION-FUNDED COMPONENTS */}
      <section className="px-6 py-16" data-testid="institution-funded-section">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.institutionFunded.eyebrow} title={d.institutionFunded.title} desc={d.institutionFunded.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-purple text-white"><Check className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.institutionFunded.required.title}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {d.institutionFunded.required.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 flex-none text-purple" /> {r}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-cyan text-white"><Plus className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.institutionFunded.optional.title}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {d.institutionFunded.optional.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><span className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-cyan" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDED ACCESS — Complete software access through June 2027 */}
      <section className="px-6 py-16 bg-white" data-testid="included-access-section">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.includedAccess.eyebrow} title={d.includedAccess.title} desc={d.includedAccess.desc} eyebrowColor="mint" />
          <div className="mt-8 rounded-3xl bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-6 md:p-8 ring-1 ring-mint/20">
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-mint text-white shadow"><Check className="h-4.5 w-4.5" /></span>
              <h3 className="text-[16px] font-bold text-navy">{d.includedAccess.listTitle}</h3>
            </div>
            <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {d.includedAccess.items.map((r, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl bg-white/70 backdrop-blur-sm px-3 py-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 flex-none text-mint" /> {r}</li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <GradientButton to={d.includedAccess.ctaPrimary.to}>{d.includedAccess.ctaPrimary.label}</GradientButton>
              <GradientButton to={d.includedAccess.ctaSecondary.to} variant="outline">{d.includedAccess.ctaSecondary.label}</GradientButton>
            </div>
          </div>
        </div>
      </section>

      {/* DEVELOPED AT IIT GUWAHATI */}
      <section className="px-6 py-16" data-testid="developed-at-section">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr] items-center">
            <div>
              <Eyebrow color="royal">{d.developedAt.eyebrow}</Eyebrow>
              <div className="mt-2 flex items-start gap-4">
                <img
                  src="https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/7wsxk02r_IITG%20logo.png"
                  alt="IIT Guwahati logo"
                  loading="lazy"
                  className="h-14 w-14 shrink-0 object-contain"
                  data-testid="school-developedat-iitg-logo"
                />
                <h2 className="text-[28px] md:text-[34px] font-extrabold leading-tight tracking-tight text-navy">{d.developedAt.title}</h2>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-500">{d.developedAt.desc}</p>
              <div className="mt-6 rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className="text-[15px] font-bold text-navy">{d.developedAt.highlightTitle}</div>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-500">{d.developedAt.highlightDesc}</p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={d.developedAt.ctaPrimary.to}>{d.developedAt.ctaPrimary.label}</GradientButton>
                <GradientButton to={d.developedAt.ctaSecondary.to} variant="outline">{d.developedAt.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-3 shadow-soft overflow-hidden">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img src={TEAM_PHOTO_URL} alt="Team and school co-design" className="block w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="px-3 pb-2 pt-3 text-center text-[11.5px] font-semibold text-ink-500">{d.developedAt.imageLabel}</div>
            </div>
          </div>
        </div>
      </section>

      {/* LEARNING ANALYTICS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.analytics.eyebrow} title={d.analytics.title} desc={d.analytics.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-white shadow-soft overflow-hidden">
              <div className="aspect-[16/10] bg-navy p-2">
                <div className="h-full rounded-md overflow-hidden bg-white">
                  <img src={LAPTOP_URL} alt="Teacher dashboard preview" className="block w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[15px] font-bold text-navy">{d.analytics.listTitle}</div>
              <ul className="mt-4 space-y-2.5">
                {d.analytics.list.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* REAL SCHOOL USE — 4 compact videos in a single row */}
      <section className="px-6 py-16" data-testid="real-use-section">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.realUse.eyebrow} title={d.realUse.title} desc={d.realUse.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="real-use-row">
            {/* Main deployment video (first tile) */}
            <div className="rounded-2xl border border-canvas-200 bg-white shadow-soft overflow-hidden transition-transform hover:-translate-y-0.5 hover:shadow-card">
              <div className="aspect-video bg-brand-gradient grain flex items-center justify-center relative">
                <button className="grid h-11 w-11 place-items-center rounded-full bg-white/95 shadow-soft" aria-label="Play"><PlayCircle className="h-6 w-6 text-navy" /></button>
                <span className="absolute top-2 left-2 rounded-full bg-white/95 px-2 py-0.5 text-[9.5px] font-bold tracking-wider text-navy">FEATURE</span>
              </div>
              <div className="p-3">
                <div className="text-[13px] font-bold text-navy leading-tight">{d.realUse.mainVideo.label}</div>
                <div className="mt-1 text-[11px] text-ink-500 line-clamp-2">{d.realUse.mainVideo.desc}</div>
              </div>
            </div>
            {/* 3 student clips */}
            {d.realUse.clips.map((c, i) => (
              <button key={i} type="button" onClick={() => setOpenClip(STUDENT_CLIPS[i])} data-testid={`school-student-clip-${i+1}`} aria-label={`Play ${c.title}`} className="group rounded-2xl border border-canvas-200 bg-white shadow-soft overflow-hidden transition-transform hover:-translate-y-0.5 hover:shadow-card text-left focus:outline-none focus:ring-4 focus:ring-purple/30">
                <div className="relative aspect-video overflow-hidden">
                  <img src={youtubeThumb(STUDENT_CLIPS[i], 'hqdefault')} alt={c.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
                  <span className="absolute inset-0 grid place-items-center"><span className="grid h-11 w-11 place-items-center rounded-full bg-white/95 shadow-soft"><PlayCircle className="h-6 w-6 text-navy" /></span></span>
                </div>
                <div className="p-3">
                  <div className="text-[13px] font-bold text-navy">{c.title}</div>
                  <div className="text-[11px] text-ink-500">Real classroom moment</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SCHOOL DEPLOYMENT metrics */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.deployment.eyebrow} title={d.deployment.title} desc={d.deployment.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {d.deployment.metrics.map((m, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className="text-[26px] md:text-[28px] font-extrabold text-gradient">{m.k}</div>
                <div className="mt-1 text-[12.5px] font-bold text-navy">{m.label}</div>
                <div className="mt-1 text-[11.5px] text-ink-500">{m.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOGNITION / Press */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.press.eyebrow} title={d.press.title} desc={d.press.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {d.press.items.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`school-press-${i}`}
                className="rounded-2xl border border-canvas-200 bg-white p-4 text-center shadow-soft hover:-translate-y-0.5 hover:shadow-card transition-all focus:outline-none focus:ring-4 focus:ring-purple/20"
              >
                <div className={`mx-auto h-8 w-8 rounded-md ${colorBg50[p.color]}`} />
                <div className="mt-3 text-[10.5px] font-bold tracking-wider text-navy">{p.label}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner
            title={d.cta.title}
            desc={d.cta.desc}
            primary={d.cta.primary}
            secondary={d.cta.secondary}
          />
        </div>
      </section>

      {heroOpen && <VideoLightbox videoId={FEATURED_VIDEO_ID} title="View Gyan Dhara in action" onClose={() => setHeroOpen(false)} />}
      {openClip && <VideoLightbox videoId={openClip} title="Student clip" onClose={() => setOpenClip(null)} />}
    </PageShell>
  );
}
